import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectUser } from "../redux/slices/authSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDashboardPromoters, getDashboardStats } from "../services/api";

import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Topbar from "../components/Admin/Topbar/Topbar";
import DashboardView from "../components/Admin/DashboardView/DashboardView";
import UsersView from "../components/Admin/UsersView/UsersView";
import SalesReportsView from "../components/Admin/SalesReportsView/SalesReportsView";
import SessionsView from "../components/Admin/SessionsView/SessionsView";
import OutletRadiusView from "../components/Admin/OutletRadiusView/OutletRadiusView";
import DisplayProofsView from "../components/Admin/DisplayProofsView/DisplayProofsView";
import TemporaryView from "../components/Admin/TemporaryView/TemporaryView";

import "../components/Admin/Admin.css";

const MENU = {
  DASHBOARD: "DASHBOARD",
  SALES_REPORTS: "SALES_REPORTS",
  SESSIONS: "SESSIONS",
  OUTLET_RADIUS: "OUTLET_RADIUS",
  DISPLAY_PROOFS: "DISPLAY_PROOFS",
  USERS: "USERS",
};

const formatPromoterHelperValue = (promoter) =>
  promoter?.email ? `${promoter.full_name} (${promoter.email})` : promoter?.full_name || "";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const [activeMenu, setActiveMenu] = useState(MENU.DASHBOARD);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth > 768;
  });
  const [globalSearchInput, setGlobalSearchInput] = useState("");
  const [searchIntentsByMenu, setSearchIntentsByMenu] = useState({
    [MENU.SALES_REPORTS]: null,
    [MENU.SESSIONS]: null,
    [MENU.OUTLET_RADIUS]: null,
    [MENU.DISPLAY_PROOFS]: null,
    [MENU.USERS]: null,
  });
  const [loading, setLoading] = useState(true);

  const [dashboardStats, setDashboardStats] = useState({
    totalSales: 0,
    activeSessions: 0,
    totalSessions: 0,
    visitsToday: 0,
    status: "not_active",
    deltas: {
      salesPct: 0,
      sessionsPct: 0,
      visitsPct: 0,
    },
    productsSoldToday: [],
  });

  const [executives, setExecutives] = useState([]);
  const [selectedPromoterId, setSelectedPromoterId] = useState("");
  const [selectedPromoterName, setSelectedPromoterName] = useState("");
  const [promoterInput, setPromoterInput] = useState("");
  const [promoterOptions, setPromoterOptions] = useState([]);
  const [promoterOptionsLoading, setPromoterOptionsLoading] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await getDashboardStats({
          promoterId: selectedPromoterId || undefined,
        });
        if (response.success) {
          setDashboardStats({
            totalSales: response.data.totalSales,
            activeSessions: response.data.activeSessions,
            totalSessions: response.data.totalSessions,
            visitsToday: response.data.visitsToday,
            status: response.data.status || "not_active",
            deltas: response.data.deltas || {
              salesPct: 0,
              sessionsPct: 0,
              visitsPct: 0,
            },
            productsSoldToday:
              response.data.productsSoldToday || response.data.topProducts || [],
          });
          setExecutives(response.data.promoters || []);
          const selectedPromoter = response.data.selectedPromoter || null;
          const selectedName = selectedPromoter?.full_name || "";
          setSelectedPromoterName(selectedName);
          if (selectedPromoter) {
            setPromoterInput(formatPromoterHelperValue(selectedPromoter));
          } else if (!selectedPromoterId) {
            setPromoterInput("");
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    if (activeMenu === MENU.DASHBOARD) {
      fetchDashboardData();
    }
  }, [activeMenu, selectedPromoterId]);

  useEffect(() => {
    if (activeMenu !== MENU.DASHBOARD) return;

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        setPromoterOptionsLoading(true);
        const response = await getDashboardPromoters({
          q: promoterInput,
          limit: 20,
        });

        if (!isCancelled) {
          setPromoterOptions(response.data || []);
        }
      } catch (error) {
        if (!isCancelled) {
          setPromoterOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setPromoterOptionsLoading(false);
        }
      }
    }, 250);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [activeMenu, promoterInput]);

  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "super_admin") {
      navigate("/");
    }
  }, [user, navigate]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const handleOpenSalesReports = (search = "") => {
    const nextQuery = String(search || "").trim();
    const nextIntent = {
      intentId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      scope: "sales",
      targetMenu: MENU.SALES_REPORTS,
      query: nextQuery,
      item: null,
      navigationType: nextQuery ? "drilldown" : "direct",
    };

    setSearchIntentsByMenu((previous) => ({
      ...previous,
      [MENU.SALES_REPORTS]: nextIntent,
    }));
    setGlobalSearchInput(nextQuery);
    setActiveMenu(MENU.SALES_REPORTS);
  };

  const handleGlobalSearchNavigate = useCallback(
    ({ scope, targetMenu, query, item, navigationType }) => {
      const nextQuery = typeof query === "string" ? query : "";
      const resolvedTargetMenu =
        targetMenu && Object.values(MENU).includes(targetMenu)
          ? targetMenu
          : null;

      setGlobalSearchInput(nextQuery);
      if (!resolvedTargetMenu) return;

      const nextIntent = {
        intentId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        scope: String(scope || "").toLowerCase(),
        targetMenu: resolvedTargetMenu,
        query: String(nextQuery || "").trim(),
        item: item || null,
        navigationType: navigationType || "item",
      };

      setSearchIntentsByMenu((previous) => ({
        ...previous,
        [resolvedTargetMenu]: nextIntent,
      }));
      setActiveMenu(resolvedTargetMenu);
    },
    [],
  );

  const handleSearchIntentHandled = useCallback((menu, intentId) => {
    setSearchIntentsByMenu((previous) => {
      const current = previous[menu];
      if (!current) return previous;
      if (current.intentId !== intentId) return previous;

      return {
        ...previous,
        [menu]: null,
      };
    });
  }, []);

  const resolvePromoterFromOptions = (rawInput, options) => {
    const normalizedInput = String(rawInput || "").trim();
    if (!normalizedInput) return null;
    const normalizedLower = normalizedInput.toLowerCase();
    const list = Array.isArray(options) ? options : [];
    const exactMatch =
      list.find(
        (promoter) =>
          formatPromoterHelperValue(promoter).toLowerCase() === normalizedLower ||
          String(promoter.full_name || "").trim().toLowerCase() === normalizedLower ||
          String(promoter.email || "").trim().toLowerCase() === normalizedLower,
      ) || null;

    if (exactMatch) return exactMatch;

    const partialMatches = list.filter((promoter) => {
      const helperValue = formatPromoterHelperValue(promoter).toLowerCase();
      const fullName = String(promoter.full_name || "").toLowerCase();
      const email = String(promoter.email || "").toLowerCase();
      return (
        helperValue.includes(normalizedLower) ||
        fullName.includes(normalizedLower) ||
        email.includes(normalizedLower)
      );
    });

    return partialMatches.length === 1 ? partialMatches[0] : null;
  };

  const handleApplyPromoterFilter = async (overrideInput = null) => {
    const sourceInput = overrideInput === null ? promoterInput : overrideInput;
    const trimmedInput = String(sourceInput || "").trim();
    if (!trimmedInput) {
      setSelectedPromoterId("");
      setSelectedPromoterName("");
      return;
    }

    const localMatch = resolvePromoterFromOptions(trimmedInput, promoterOptions);
    if (localMatch) {
      setSelectedPromoterId(String(localMatch.user_id));
      setSelectedPromoterName(localMatch.full_name || "");
      setPromoterInput(formatPromoterHelperValue(localMatch));
      return;
    }

    try {
      setPromoterOptionsLoading(true);
      const response = await getDashboardPromoters({ q: trimmedInput, limit: 20 });
      const remoteOptions = response?.data || [];
      setPromoterOptions(remoteOptions);
      const remoteMatch = resolvePromoterFromOptions(trimmedInput, remoteOptions);
      if (remoteMatch) {
        setSelectedPromoterId(String(remoteMatch.user_id));
        setSelectedPromoterName(remoteMatch.full_name || "");
        setPromoterInput(formatPromoterHelperValue(remoteMatch));
      }
    } catch (error) {
      console.error("Error applying promoter filter:", error);
    } finally {
      setPromoterOptionsLoading(false);
    }
  };

  const handleClearPromoterFilter = () => {
    setPromoterInput("");
    setSelectedPromoterId("");
    setSelectedPromoterName("");
  };

  const handlePromoterInputChange = (value) => {
    setPromoterInput(value);
    if (selectedPromoterId) {
      setSelectedPromoterId("");
      setSelectedPromoterName("");
    }
  };

  return (
    <div className="admin-container">
      <Sidebar
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        user={user}
        onLogout={handleLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`admin-main ${sidebarOpen ? "" : "admin-main-expanded"}`}>
        <Topbar
          searchQuery={globalSearchInput}
          setSearchQuery={setGlobalSearchInput}
          onSearchNavigate={handleGlobalSearchNavigate}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <div className="admin-content-wrapper">
          {activeMenu === MENU.DASHBOARD && (
            <div className="admin-page-header">
              <h1 className="admin-page-title">{t("admin.dashboard.title")}</h1>
              <p className="admin-page-subtitle">{t("admin.dashboard.subtitle")}</p>
            </div>
          )}

          {activeMenu === MENU.DASHBOARD && (
            <DashboardView
              dashboardStats={dashboardStats}
              executives={executives}
              searchQuery=""
              loading={loading}
              onOpenSalesReports={handleOpenSalesReports}
              selectedPromoterId={selectedPromoterId}
              selectedPromoterName={selectedPromoterName}
              promoterInput={promoterInput}
              onPromoterInputChange={handlePromoterInputChange}
              onApplyPromoterFilter={handleApplyPromoterFilter}
              onClearPromoterFilter={handleClearPromoterFilter}
              promoterOptions={promoterOptions}
              promoterOptionsLoading={promoterOptionsLoading}
            />
          )}

          {activeMenu === MENU.SALES_REPORTS && (
            <SalesReportsView
              user={user}
              searchIntent={searchIntentsByMenu[MENU.SALES_REPORTS]}
              onSearchIntentHandled={(intentId) =>
                handleSearchIntentHandled(MENU.SALES_REPORTS, intentId)
              }
            />
          )}

          {activeMenu === MENU.SESSIONS && (
            <SessionsView
              user={user}
              searchIntent={searchIntentsByMenu[MENU.SESSIONS]}
              onSearchIntentHandled={(intentId) =>
                handleSearchIntentHandled(MENU.SESSIONS, intentId)
              }
            />
          )}

          {activeMenu === MENU.OUTLET_RADIUS && (
            <OutletRadiusView searchQuery={globalSearchInput} />
          )}

          {activeMenu === MENU.DISPLAY_PROOFS && (
            <DisplayProofsView
              user={user}
              searchIntent={searchIntentsByMenu[MENU.DISPLAY_PROOFS]}
              onSearchIntentHandled={(intentId) =>
                handleSearchIntentHandled(MENU.DISPLAY_PROOFS, intentId)
              }
            />
          )}

          {activeMenu === MENU.USERS && (
            <UsersView
              searchIntent={searchIntentsByMenu[MENU.USERS]}
              onSearchIntentHandled={(intentId) =>
                handleSearchIntentHandled(MENU.USERS, intentId)
              }
            />
          )}

          {activeMenu !== MENU.DASHBOARD &&
            activeMenu !== MENU.USERS &&
            activeMenu !== MENU.SALES_REPORTS &&
            activeMenu !== MENU.SESSIONS &&
            activeMenu !== MENU.OUTLET_RADIUS &&
            activeMenu !== MENU.DISPLAY_PROOFS && (
            <TemporaryView type={activeMenu} />
          )}
        </div>
      </div>
    </div>
  );
}
